import {
  useOrganisation,
  usePermissions,
  useUpdateOrganisationTmsPlanFeatures,
} from '@my/directory-api/hooks';
import { Button, ErrorBanner, FormSection, Loading, Switch, showToast } from '@my/ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  parsePlanFeatures,
  serialisePlanFeatures,
  type PlanFeatures,
} from '@/features/plan/lib/planFeatures';
import { POOL_ORDER, type PoolId } from '@/features/plan/lib/planResources';

export interface PlanFeaturesSectionProps {
  /** Owning organisation id; the card stays disabled while this is null. */
  organisationId: string | null;
}

/**
 * What the Plan cockpit offers this organisation — the six resource-strip
 * pools, plus the driver Gantt.
 *
 * <p>
 * STRATUM SUPPORT ONLY, and it asks here, exactly as `WorkViewTabsSection`
 * does. Same reasoning: the endpoint enforces the rule and answers 404 to a
 * customer token, so this is the affordance rather than the defence, and
 * opening it to Owners later is deleting the guard below.
 * </p>
 *
 * <p>
 * A pool switched ON here is OFFERED, not forced into view — the strip still
 * hides a pool that has nothing in it today. So the honest reading of these is
 * "may this organisation ever see this tab", which is why the labels say
 * offered rather than shown.
 * </p>
 *
 * <p>
 * No "at least one" rule, unlike the Work View tabs: an organisation that
 * assigns purely from the Gantt is a coherent setup, and the strip renders a
 * bare band rather than breaking.
 * </p>
 */
export function PlanFeaturesSection({ organisationId }: PlanFeaturesSectionProps) {
  const { t } = useTranslation(['configuration', 'common']);
  const { role } = usePermissions();
  const organisationQuery = useOrganisation(organisationId);
  const saveMutation = useUpdateOrganisationTmsPlanFeatures();

  const stored = useMemo(
    () => parsePlanFeatures(organisationQuery.data?.tmsPlanFeaturesJson),
    [organisationQuery.data?.tmsPlanFeaturesJson],
  );

  const [draft, setDraft] = useState<PlanFeatures>(stored);

  // Re-seed whenever the server's copy changes — first load, and after a save
  // invalidates the query. Keyed on the serialised document rather than the
  // object so a refetch returning identical data does not discard an edit in
  // progress.
  const storedKey = serialisePlanFeatures(stored);
  useEffect(() => {
    setDraft(parsePlanFeatures(storedKey));
  }, [storedKey]);

  const isDirty = serialisePlanFeatures(draft) !== storedKey;

  if (role !== 'SuperUser') return null;

  const togglePool = (pool: PoolId, next: boolean) =>
    setDraft((prev) => ({ ...prev, pools: { ...prev.pools, [pool]: next } }));

  const handleSave = async () => {
    if (!organisationId) return;
    try {
      await saveMutation.mutateAsync({
        organisationId,
        featuresJson: serialisePlanFeatures(draft),
      });
      showToast({ type: 'success', message: t('configuration:planFeatures.saved') });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('configuration:planFeatures.saveFailed'),
      });
    }
  };

  return (
    <FormSection
      title={t('configuration:planFeatures.title')}
      columns={3}
      disabled={!organisationId}
      footer={
        <Button
          type="button"
          disabled={!isDirty || !organisationId}
          loading={saveMutation.isPending}
          onClick={handleSave}
        >
          {t('common:actions.save')}
        </Button>
      }
    >
      <p className="text-sm text-slate-500 md:col-span-3">
        {t('configuration:planFeatures.description')}
      </p>

      {organisationQuery.isPending ? (
        <div className="md:col-span-3">
          <Loading label={t('common:states.loading')} />
        </div>
      ) : organisationQuery.isError ? (
        <div className="md:col-span-3">
          <ErrorBanner
            title={t('configuration:planFeatures.loadFailed')}
            message={organisationQuery.error.message}
          />
        </div>
      ) : (
        <>
          {POOL_ORDER.map((pool) => (
            <Switch
              key={pool}
              name={`planPool-${pool}`}
              label={t(`configuration:planFeatures.pools.${pool}`)}
              checked={draft.pools[pool]}
              enabledLabel={t('common:states.active')}
              disabledLabel={t('common:states.inactive')}
              onChange={(e) => togglePool(pool, e.target.checked)}
            />
          ))}

          {/* The Gantt is not a pool — it is the timeline above the strip — so
              it sits on its own row rather than pretending to be a seventh. */}
          <div className="md:col-span-3 md:w-1/3">
            <Switch
              name="planGantt"
              label={t('configuration:planFeatures.gantt')}
              checked={draft.gantt}
              enabledLabel={t('common:states.active')}
              disabledLabel={t('common:states.inactive')}
              onChange={(e) => setDraft((prev) => ({ ...prev, gantt: e.target.checked }))}
            />
          </div>
        </>
      )}
    </FormSection>
  );
}
